import Box from '@zenbusiness/zui/Box';
import IconButton from '@zenbusiness/zui/IconButton';
import { superstyled } from '@zenbusiness/zui/styles';

import { VELO_SIDE_PANEL_TOP_BAR_HEIGHT } from './constants';

const THINKING_STEPS_HEIGHT = '1.125rem';

export const Root = superstyled({
  name: 'VeloSidePanel',
  slot: 'Root',
  tag: Box,
  styles: ({ theme }) => ({
    gridArea: 'velo',
    position: 'sticky',
    top: 0,
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.border.main}`,
  }),
});

export const TopBar = superstyled({
  name: 'VeloSidePanel',
  slot: 'TopBar',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    height: VELO_SIDE_PANEL_TOP_BAR_HEIGHT,
    padding: theme.spacing(0, 4),
    color: theme.palette.common.white,
    background: `radial-gradient(120% 260% at 100% 0%, rgba(57, 113, 231, 0.24) 0%, rgba(37, 65, 125, 0.24) 50%, rgba(17, 17, 18, 0.24) 100%), ${theme.palette.primary.main}`,
  }),
});

export const Logo = superstyled({
  name: 'VeloSidePanel',
  slot: 'Logo',
  tag: Box,
  styles: () => ({
    display: 'flex',
    alignItems: 'center',
  }),
});

export const Actions = superstyled({
  name: 'VeloSidePanel',
  slot: 'Actions',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
});

export const Body = superstyled({
  name: 'VeloSidePanel',
  slot: 'Body',
  tag: Box,
  styles: ({ theme }) => ({
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',

    '& .VeloQA-root': {
      height: `calc(100dvh - ${VELO_SIDE_PANEL_TOP_BAR_HEIGHT})`,
      overflowY: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      padding: theme.spacing(2.5, 3, 1.5),
      border: 'none',
      borderRadius: 0,
      background: 'none',
    },
    '& .VeloQA-logoContainer': {
      display: 'none',
    },
    '& .VeloQA-headerSection': {
      order: -2,
      marginBottom: theme.spacing(1.5),
    },
    '& .VeloQA-titleContainer': {
      alignItems: 'center',
      textAlign: 'center',
      '& > *': {
        color: theme.palette.text.secondary,
      },
      '& > :first-child': {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        fontWeight: 600,
      },
      '& > :last-child': {
        marginTop: theme.spacing(3),
        fontSize: '1rem',
        lineHeight: '1.5rem',
      },
    },
    '& .VeloQA-promptsContainer': {
      order: -1,
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'stretch',
      alignItems: 'stretch',
      gap: theme.spacing(2),
    },
    '& .VeloQA-promptChip': {
      width: '100%',
      minHeight: '4.625rem',
      padding: theme.spacing(1.5, 4),
      justifyContent: 'flex-start',
      textAlign: 'left',
      borderRadius: '0.75rem',
      fontSize: '1rem',
      lineHeight: '1.5rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      border: '1px solid transparent',
      background: `linear-gradient(${theme.palette.common.white}, ${theme.palette.common.white}) padding-box, linear-gradient(249deg, #3971E7 27.22%, #33F28B 100%) border-box`,
      '&:hover': {
        background: `linear-gradient(${theme.palette.common.white}, ${theme.palette.common.white}) padding-box, linear-gradient(249deg, #3971E7 27.22%, #33F28B 100%) border-box`,
        boxShadow: theme.shadows[2],
      },
    },
    '& .VeloQA-root > *': {
      flexShrink: 0,
    },
    '& .VeloQA-root > :last-child': {
      marginTop: theme.spacing(-1),
      marginBottom: 0,
    },
    '& .VeloQA-inputContainer': {
      width: 'auto',
      marginTop: 'auto',
      marginInline: theme.spacing(-1.5),
    },
    '& .VeloQA-root:not(:has(.VeloQA-answerArea)) .VeloQA-inputContainer': {
      marginBottom: `calc(${THINKING_STEPS_HEIGHT} + ${theme.spacing(1.5)})`,
    },
    '& .VeloQA-answerArea:not(:has(.VeloQA-responseContainer))': {
      minHeight: THINKING_STEPS_HEIGHT,
      marginTop: theme.spacing(-1),
    },
    '& .VeloQA-answerArea:has(.VeloQA-responseContainer)': {
      flexGrow: 1,
    },
    '& .VeloQA-answerArea[data-expanded="false"]:has(.VeloQA-responseContainer)': {
      flexBasis: 0,
      flexShrink: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    '& .VeloQA-answerArea[data-expanded="false"] .VeloQA-responseContainer': {
      flex: '0 1 auto',
      minHeight: 0,
      maxHeight: 'none',
    },
    '& .VeloQA-expandButtonContainer': {
      flexShrink: 0,
    },
    '& .VeloQA-responseContainer::after': {
      background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
    },
  }),
});

export const ActionButton = superstyled({
  name: 'VeloSidePanel',
  slot: 'ActionButton',
  tag: IconButton,
  styles: () => ({
    width: '3rem',
    height: '2.25rem',
    padding: 0,
    border: '1px solid rgba(255, 255, 255, 0.24)',
    borderRadius: '0.75rem',
    color: 'rgba(255, 255, 255, 0.93)',
    '& svg': {
      fontSize: '1.25rem',
    },
  }),
});
